import { useCallback, useEffect, useRef, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import { extractOptionsPositionHistoryList, extractOptionsTransactionHistoryList } from "./optionsDataHelpers";

const OPTIONS_POLL_MS = 3000;

const EXERCISE_TAB = "exerciseHistory";

const HISTORY_POLL_TABS = new Set([
    "positionHistory",
    "orderHistory",
    "tradeHistory",
    EXERCISE_TAB,
]);

/**
 * REST history polling — 3s interval while tab is active (futures trade pattern).
 * Trade history uses GET /v1/options/trades per OPTIONS_ORDER_API.md.
 * Transaction history fetches once when the tab opens (no polling).
 */
export default function useOptionsHistory({
    token,
    activeTab,
    underlying,
    symbol,
    pollMs = OPTIONS_POLL_MS,
}) {
    const [positionHistory, setPositionHistory] = useState([]);
    const [orderHistory, setOrderHistory] = useState([]);
    const [tradeHistory, setTradeHistory] = useState([]);
    const [exerciseHistory, setExerciseHistory] = useState([]);
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);

    const buildParams = useCallback(
        () => ({
            skip: 0,
            limit: 50,
            ...(underlying ? { underlying } : {}),
            ...(symbol ? { symbol } : {}),
        }),
        [underlying, symbol]
    );

    const buildPositionHistoryParams = useCallback(
        () => ({
            skip: 0,
            limit: 50,
            status: "CLOSED,LIQUIDATED,EXPIRED",
        }),
        []
    );

    const buildTransactionHistoryParams = useCallback(
        () => ({
            page: 1,
            limit: 50,
            asset: "USDT",
        }),
        []
    );

    const fetchTab = useCallback(async (tab) => {
        if (!token) return;

        let fetcher;
        if (tab === "positionHistory") fetcher = () => AuthService.optionsPositionHistory(buildPositionHistoryParams());
        else if (tab === "orderHistory") {
            fetcher = () =>
                AuthService.optionsOrderHistory({
                    skip: 0,
                    limit: 50,
                    ...(symbol ? { symbol } : {}),
                    status: "FILLED,CANCELLED,REJECTED,EXPIRED",
                });
        }
        else if (tab === "tradeHistory") fetcher = () => AuthService.optionsLiveTrades(buildParams());
        else if (tab === EXERCISE_TAB) fetcher = () => AuthService.optionsExerciseHistory(buildParams());
        else if (tab === "transactionHistory") {
            fetcher = () => AuthService.optionsTransactionHistory(buildTransactionHistoryParams());
        }
        else return;

        setLoading(true);
        try {
            const res = await fetcher();
            if (!mountedRef.current) return;
            let list;
            if (tab === "positionHistory") {
                list = extractOptionsPositionHistoryList(res);
            } else if (tab === "transactionHistory") {
                list = extractOptionsTransactionHistoryList(res);
            } else {
                list =
                    res?.data?.orders ??
                    res?.data?.trades ??
                    res?.data?.exercises ??
                    res?.data?.transactions ??
                    res?.data?.items ??
                    (Array.isArray(res?.data) ? res.data : []);
            }

            if (tab === "positionHistory") setPositionHistory(list);
            else if (tab === "orderHistory") setOrderHistory(list);
            else if (tab === "tradeHistory") setTradeHistory(list);
            else if (tab === EXERCISE_TAB) setExerciseHistory(list);
            else if (tab === "transactionHistory") setTransactionHistory(list);
        } catch {
            /* keep existing rows */
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [token, symbol, buildParams, buildPositionHistoryParams, buildTransactionHistoryParams]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!token || !HISTORY_POLL_TABS.has(activeTab)) return undefined;

        fetchTab(activeTab);
        const id = window.setInterval(() => fetchTab(activeTab), pollMs);
        return () => window.clearInterval(id);
    }, [token, activeTab, fetchTab, pollMs]);

    useEffect(() => {
        if (!token || activeTab !== "transactionHistory") return;
        fetchTab("transactionHistory");
    }, [token, activeTab, fetchTab]);

    return {
        loading,
        positionHistory,
        orderHistory,
        tradeHistory,
        exerciseHistory,
        transactionHistory,
        refetch: fetchTab,
    };
}
